import React, { useState, useEffect } from 'react';
import {
    getProzesse,
    createProzess,
    updateProzess,
    deleteProzess,
    getAnalysen,
    createAnalyse,
    updateAnalyse
} from '../../../../../services/schutzbedarfsanalyseService';
import ProzessModal from './ProzessModal';
import ProzessanalyseModal from './ProzessanalyseModal';
import './ProzesseSection.css';

const ProzesseSection = () => {
    const [prozesse, setProzesse] = useState([]);
    const [analysen, setAnalysen] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedProzesse, setExpandedProzesse] = useState(new Set());

    const [showModal, setShowModal] = useState(false);
    const [editingProzess, setEditingProzess] = useState(null);

    const [showAnalyseModal, setShowAnalyseModal] = useState(false);
    const [selectedProzess, setSelectedProzess] = useState(null);
    const [editingAnalyse, setEditingAnalyse] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        const [prozesseResult, analysenResult] = await Promise.all([
            getProzesse(),
            getAnalysen(),
        ]);

        if (prozesseResult.success) {
            setProzesse(prozesseResult.data);
        } else {
            setError(prozesseResult.error);
        }

        if (analysenResult.success) {
            setAnalysen(analysenResult.data);
        }

        setLoading(false);
    };

    const toggleProzess = (prozessId) => {
        const newExpanded = new Set(expandedProzesse);
        if (newExpanded.has(prozessId)) {
            newExpanded.delete(prozessId);
        } else {
            newExpanded.add(prozessId);
        }
        setExpandedProzesse(newExpanded);
    };

    const handleCreate = () => {
        setEditingProzess(null);
        setShowModal(true);
    };

    const handleEdit = (prozess, e) => {
        e.stopPropagation();
        setEditingProzess(prozess);
        setShowModal(true);
    };

    const handleSave = async (prozessData) => {
        let result;

        if (editingProzess) {
            result = await updateProzess(editingProzess.prozess_id, prozessData);
        } else {
            result = await createProzess(prozessData);
        }

        if (result.success) {
            setShowModal(false);
            setEditingProzess(null);
            loadData();
        } else {
            alert(result.error);
        }
    };

    const handleDelete = async (prozess, e) => {
        e.stopPropagation();
        if (!window.confirm(`Prozess "${prozess.prozess_name}" wirklich löschen?`)) return;

        const result = await deleteProzess(prozess.prozess_id);

        if (result.success) {
            loadData();
        } else {
            alert(result.error);
        }
    };

    const handleAnalyseProzess = (prozess, e) => {
        e.stopPropagation();
        const existingAnalyse = analysen.find(a => a.prozess_id === prozess.prozess_id);
        setSelectedProzess(prozess);
        setEditingAnalyse(existingAnalyse || null);
        setShowAnalyseModal(true);
    };

    const handleSaveAnalyse = async (analyseData) => {
        let result;

        if (editingAnalyse) {
            result = await updateAnalyse(editingAnalyse.analyse_id, analyseData);
        } else {
            result = await createAnalyse({
                ...analyseData,
                prozess_id: selectedProzess.prozess_id,
            });
        }

        if (result.success) {
            setShowAnalyseModal(false);
            setSelectedProzess(null);
            setEditingAnalyse(null);
            loadData();
        } else {
            alert('Fehler beim Speichern: ' + result.error);
        }
    };

    const getAnalyseForProzess = (prozessId) => {
        return analysen.find(a => a.prozess_id === prozessId);
    };

    const getSchutzbedarfBadge = (level) => {
        if (!level) return <span className="badge badge-none">Nicht bewertet</span>;

        const config = {
            'sehr_hoch': { label: 'A2 C4 I3 N3', color: 'badge-sehr-hoch' },
            'hoch': { label: 'A2 C3 I3 N3', color: 'badge-hoch' },
            'normal': { label: 'A2 C1 I2 N2', color: 'badge-normal' },
            'gering': { label: 'A2 C1N3', color: 'badge-gering' },
        };

        const badge = config[level] || { label: 'Nicht bewertet', color: 'badge-none' };

        return <span className={`badge ${badge.color}`}>{badge.label}</span>;
    };

    const getStatusBadge = (analyse) => {
        if (!analyse) return <span className="badge badge-gray">Nicht analysiert</span>;

        const config = {
            'entwurf': { label: 'Entwurf', color: 'badge-gray' },
            'in_bearbeitung': { label: 'In Bearbeitung', color: 'badge-yellow' },
            'abgeschlossen': { label: 'Abgeschlossen', color: 'badge-green' },
        };

        const status = config[analyse.status] || { label: 'Unbekannt', color: 'badge-gray' };

        return <span className={`badge ${status.color}`}>{status.label}</span>;
    };

    const groupProzesseByCategory = () => {
        const grouped = {};
        prozesse.forEach(prozess => {
            const category = prozess.prozess_kategorie || 'Ohne Kategorie';
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(prozess);
        });
        return grouped;
    };

    if (loading) {
        return (
            <div className="prozesse-section">
                <div className="loading-state">Lade Prozesse...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="prozesse-section">
                <div className="error-state">
                    <p>Fehler beim Laden: {error}</p>
                    <button onClick={loadData} className="btn-retry">Erneut versuchen</button>
                </div>
            </div>
        );
    }

    const groupedProzesse = groupProzesseByCategory();

    return (
        <div className="prozesse-section">
            <div className="section-header-bar">
                <div className="section-title">
                    <h2>Geschäftsprozesse</h2>
                </div>
                <button onClick={handleCreate} className="btn-export">
                    + Neuer Prozess
                </button>
            </div>

            {prozesse.length === 0 ? (
                <div className="empty-state">
                    <h3>Keine Prozesse vorhanden</h3>
                    <p>Erstellen Sie Ihren ersten Geschäftsprozess</p>
                    <button onClick={handleCreate} className="btn-primary">
                        Prozess erstellen
                    </button>
                </div>
            ) : (
                <div className="prozesse-table-container">
                    <table className="prozesse-table">
                        <thead>
                            <tr>
                                <th className="col-bezeichnung">Bezeichnung</th>
                                <th className="col-verantwortung">Verantwortung</th>
                                <th className="col-schutzbedarf">Schutzbedarf</th>
                                <th className="col-status">Status</th>
                                <th className="col-actions">Aktionen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(groupedProzesse).map(([category, categoryProzesse]) => (
                                <React.Fragment key={category}>
                                    <tr className="category-row">
                                        <td colSpan="5" className="category-header">
                                            <strong>{category}</strong>
                                        </td>
                                    </tr>
                                    {categoryProzesse.map(prozess => {
                                        const analyse = getAnalyseForProzess(prozess.prozess_id);
                                        const isExpanded = expandedProzesse.has(prozess.prozess_id);

                                        return (
                                            <React.Fragment key={prozess.prozess_id}>
                                                <tr 
                                                    className="prozess-row"
                                                    onClick={() => toggleProzess(prozess.prozess_id)}
                                                >
                                                    <td className="col-bezeichnung">
                                                        <div className="bezeichnung-cell">
                                                            <span className="expand-icon">
                                                                {isExpanded ? '▼' : '▶'}
                                                            </span>
                                                            <span className="prozess-icon">📋</span>
                                                            <span className="prozess-name">{prozess.prozess_name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="col-verantwortung">
                                                        <span className="person-icon">👤</span>
                                                        {prozess.prozess_verantwortlicher || '-'}
                                                    </td>
                                                    <td className="col-schutzbedarf">
                                                        {getSchutzbedarfBadge(prozess.gesamtschutzbedarf)}
                                                    </td>
                                                    <td className="col-status">
                                                        {getStatusBadge(analyse)}
                                                    </td>
                                                    <td className="col-actions">
                                                        <button 
                                                            className="btn-table btn-analyze"
                                                            onClick={(e) => handleAnalyseProzess(prozess, e)}
                                                        >
                                                            Analysieren
                                                        </button>
                                                        <button 
                                                            className="btn-table btn-edit"
                                                            onClick={(e) => handleEdit(prozess, e)}
                                                        >
                                                            Bearbeiten
                                                        </button>
                                                        <button 
                                                            className="btn-table btn-delete"
                                                            onClick={(e) => handleDelete(prozess, e)}
                                                        >
                                                            Löschen
                                                        </button>
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="detail-row">
                                                        <td colSpan="5">
                                                            <div className="detail-content">
                                                                {prozess.prozess_beschreibung && (
                                                                    <div className="detail-section">
                                                                        <strong>Beschreibung:</strong>
                                                                        <p>{prozess.prozess_beschreibung}</p>
                                                                    </div>
                                                                )}
                                                                {analyse && (
                                                                    <div className="detail-section">
                                                                        <strong>Schutzziele:</strong>
                                                                        <div className="schutzziele-grid">
                                                                            <div>
                                                                                <span className="schutzziel-label">Vertraulichkeit:</span>
                                                                                <span className="schutzziel-value">{analyse.vertraulichkeit}</span>
                                                                            </div>
                                                                            <div>
                                                                                <span className="schutzziel-label">Integrität:</span>
                                                                                <span className="schutzziel-value">{analyse.integritaet}</span>
                                                                            </div>
                                                                            <div>
                                                                                <span className="schutzziel-label">Verfügbarkeit:</span>
                                                                                <span className="schutzziel-value">{analyse.verfuegbarkeit}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <ProzessModal
                    prozess={editingProzess}
                    onSave={handleSave}
                    onCancel={() => {
                        setShowModal(false);
                        setEditingProzess(null);
                    }}
                />
            )}

            {showAnalyseModal && selectedProzess && (
                <ProzessanalyseModal
                    prozess={selectedProzess}
                    analyse={editingAnalyse}
                    onSave={handleSaveAnalyse}
                    onClose={() => {
                        setShowAnalyseModal(false);
                        setSelectedProzess(null);
                        setEditingAnalyse(null);
                    }}
                />
            )}
        </div>
    );
};

export default ProzesseSection;
